import { useState } from 'react';
import styled from 'styled-components';
import { useStore } from '../stores/store';
import { installAvailable, triggerInstall, isStandalone, isIOS } from '../lib/pwaInstall';
import { isOffensive } from '../lib/badwords';
import { identityFromBackup, adoptIdentity, type Identity } from '../lib/identity';
import { RestoreConfirm } from './RestoreConfirm';
import { api } from '../api/client';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 12px;
  @media (min-width: 600px) {
    align-items: center;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 18px;
  padding: 22px;
  max-height: 92vh;
  overflow-y: auto;

  h1 { font-size: 1.6rem; margin: 0 0 6px; }
  .sub { color: ${({ theme }) => theme.colors.textMuted}; font-size: 1.05rem; line-height: 1.5; }
  ul { list-style: none; padding: 0; margin: 16px 0; }
  li { padding: 6px 0; font-size: 1.05rem; display: flex; gap: 10px; align-items: center; }
`;

const Note = styled.div`
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border-left: 4px solid ${({ theme }) => theme.colors.gold};
  border-radius: 10px;
  padding: 14px;
  margin: 16px 0;
  font-size: 1.05rem;
  line-height: 1.5;
  strong { color: ${({ theme }) => theme.colors.gold}; }
`;

const InstallCTA = styled.div`
  font-size: 1.05rem;
  line-height: 1.5;
  margin-bottom: 14px;
`;

const NickField = styled.div`
  margin-bottom: 14px;
  label { display: block; font-weight: 700; margin-bottom: 6px; }
  input {
    width: 100%;
    box-sizing: border-box;
    font-size: 1.1rem;
    padding: 12px;
    border-radius: 10px;
    border: 1.5px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.bg};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Hint = styled.div`
  text-align: center;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 8px;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  width: 100%;
  border: 1.5px solid ${({ theme, $primary }) => ($primary ? theme.colors.accent : theme.colors.borderStrong)};
  background: ${({ theme, $primary }) => ($primary ? theme.colors.accent : 'transparent')};
  color: ${({ theme, $primary }) => ($primary ? theme.colors.accentText : theme.colors.text)};
  border-radius: 12px;
  padding: 13px;
  font-size: 1.05rem;
  font-weight: 700;
  margin-top: 8px;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

export function Welcome() {
  const seenWelcome = useStore((s) => s.seenWelcome);
  const setSeenWelcome = useStore((s) => s.setSeenWelcome);
  const setNick = useStore((s) => s.setNick);
  const setIntroPending = useStore((s) => s.setIntroPending);
  const [iosHelp, setIosHelp] = useState(false);
  const [nick, setNickInput] = useState('');
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [restoreMsg, setRestoreMsg] = useState('');
  // Identidad computada del respaldo + el nick que el server tiene para ella, a confirmar antes de adoptar.
  const [pending, setPending] = useState<{ id: Identity; nick: string } | null>(null);

  // No mostrar si ya lo vio o si ya está instalada (corriendo como app).
  if (seenWelcome || isStandalone()) return null;

  const nickBad = nick.trim().length >= 2 && isOffensive(nick);
  const nickOk = nick.trim().length >= 2 && !nickBad;
  const saveNick = () => {
    if (nickOk) setNick(nick.trim());
  };

  const handleInstall = async () => {
    saveNick();
    if (installAvailable()) {
      const r = await triggerInstall();
      if (r === 'accepted') {
        setIntroPending(true);
        setSeenWelcome();
      }
      return;
    }
    if (isIOS()) {
      setIosHelp(true);
      return;
    }
    // Navegadores que no exponen el prompt: guía genérica.
    setIosHelp(true);
  };

  const enter = () => {
    saveNick();
    setIntroPending(true);
    setSeenWelcome();
  };

  // Paso 1 (query): computa la identidad del respaldo SIN adoptarla y le pregunta al server quién es,
  // para confirmar antes de pisar la identidad de este equipo. Así un respaldo equivocado se ve acá
  // ("no encontramos jugador") en vez de "restaurar ok" dejando el nick viejo.
  const previewRestore = async () => {
    setRestoreMsg('');
    try {
      const id = identityFromBackup(keyInput);
      const d = await api.timbaPlayer(id.pub).catch(() => null);
      setPending({ id, nick: d?.nick ?? '' });
    } catch (e) {
      setRestoreMsg(e instanceof Error ? e.message : 'No pude leer ese respaldo.');
    }
  };

  // Paso 2 (command): el usuario confirmó → adopta la identidad, toma su nombre del server y recarga.
  const confirmRestore = () => {
    if (!pending) return;
    adoptIdentity(pending.id);
    if (pending.nick) setNick(pending.nick);
    setSeenWelcome();
    window.location.reload();
  };

  const cancelRestore = () => {
    setPending(null);
    setRestoreMsg('');
  };

  return (
    <>
    <Overlay>
      <Card>
        <h1>¡Bienvenido a VamoVamo!</h1>
        <div className="sub">
          Tu compañero para vivir el <strong>Mundial 2026</strong>. Seguí cada partido y armá tu
          propia historia del torneo.
        </div>

        <ul>
          <li>Calendario y resultados al día</li>
          <li>Zonas, posiciones y goleadores</li>
          <li>Simulá la llave: elegí quién pasa y coroná al campeón</li>
          <li>Sedes, estadios y estadísticas</li>
        </ul>

        <NickField>
          <label htmlFor="nick">Elegí un apodo</label>
          <input
            id="nick"
            type="text"
            maxLength={24}
            value={nick}
            onChange={(e) => setNickInput(e.target.value)}
            placeholder="Tu apodo"
            autoComplete="nickname"
          />
          {nickBad && <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '6px 0 0' }}>Elegí otro apodo, por favor.</p>}
        </NickField>

        <IdNote>
          No pongas datos reales, no nos interesan. Vas a crear una <strong>identidad soberana y
          privada</strong>. Si querés usarla en otro dispositivo, hacé el <strong>backup de seguridad</strong>
          {' '}(Preferencias → Tu identidad): te va a dar <strong>24 palabras</strong>, tu clave de recuperación.
        </IdNote>

        <Btn $primary onClick={enter} disabled={!nickOk}>Jugar</Btn>

        <InstallCTA>
          <strong>Opcional:</strong> agregala a tu pantalla de inicio y tené el Mundial siempre a
          mano, como una app — abre al instante y funciona aún con poca señal. (No hace falta para jugar.)
        </InstallCTA>

        {iosHelp && (
          <Note>
            {isIOS() ? (
              <>En iPhone/iPad: tocá <strong>Compartir</strong> ⬆️ y elegí{' '}
              <strong>"Agregar a inicio"</strong>.</>
            ) : (
              <>Abrí el menú del navegador (⋮) y elegí{' '}
              <strong>"Agregar a pantalla de inicio"</strong>.</>
            )}
          </Note>
        )}

        <Btn onClick={handleInstall} disabled={!nickOk}>Agregar al inicio</Btn>
        {!nickOk && <Hint>Elegí un apodo para continuar.</Hint>}

        <Restore>
          {!restoreOpen ? (
            <button type="button" className="link" onClick={() => setRestoreOpen(true)}>
              ¿Ya jugás en VamoVamo? Restaurá tu identidad
            </button>
          ) : (
            <>
              <p className="lbl">Pegá tus <strong>24 palabras</strong> (o la clave) del backup de tu otro dispositivo, para no duplicar tu usuario:</p>
              <textarea
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="tus 24 palabras (o la clave)"
                rows={3}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              {/\s/.test(keyInput.trim()) && (
                <p className="count">{keyInput.trim().split(/\s+/).length} palabras</p>
              )}
              <button type="button" className="full" onClick={previewRestore} disabled={keyInput.trim().length < 8}>Continuar</button>
              {restoreMsg && <p className="err">{restoreMsg}</p>}
            </>
          )}
        </Restore>
      </Card>
    </Overlay>
    {pending && <RestoreConfirm nick={pending.nick} onConfirm={confirmRestore} onCancel={cancelRestore} />}
    </>
  );
}

const Restore = styled.div`
  margin-top: 14px;
  text-align: center;
  .link {
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.textMuted};
    text-decoration: underline;
    font-size: 0.82rem;
    cursor: pointer;
  }
  .lbl { font-size: 0.78rem; color: ${({ theme }) => theme.colors.textMuted}; margin: 0 0 8px; line-height: 1.4; text-align: left; }
  textarea {
    width: 100%;
    box-sizing: border-box;
    resize: vertical;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 10px;
    padding: 10px 12px;
    font: inherit;
    font-size: 0.85rem;
  }
  .count { font-size: 0.72rem; color: ${({ theme }) => theme.colors.textMuted}; margin: 6px 0 0; text-align: right; }
  .full {
    width: 100%;
    margin-top: 10px;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accentText};
    border: none;
    border-radius: 10px;
    padding: 11px 14px;
    font-weight: 700;
  }
  .full:disabled { opacity: 0.5; }
  .err { color: #ff6b6b; font-size: 0.8rem; margin: 8px 0 0; }
`;

const IdNote = styled.p`
  margin: 0 0 14px;
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border-radius: 10px;
  padding: 12px;
  strong { color: ${({ theme }) => theme.colors.text}; }
`;
